import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { renderBlockHtml } from "../render/renderHtml";
import { blockRegistry } from "../registry/blocks";

const BRAND = "#069d6e";

function SortableBlock({
  block,
  settings,
  selected,
  onSelect,
  onDuplicate,
  onRemove,
}) {
  const [hovered, setHovered] = useState(false);
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const active = selected || hovered;
  const def = blockRegistry[block.type];

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: "relative",
    cursor: "pointer",
    outline: selected
      ? `2px solid ${BRAND}`
      : hovered
      ? `1px solid ${BRAND}80`
      : "1px solid transparent",
    outlineOffset: "-1px",
    backgroundColor: selected ? `${BRAND}08` : "transparent",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
    >
      {active && (
        <>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 6,
              background: selected ? BRAND : "#64748b",
              color: "#fff",
              fontSize: 11,
              lineHeight: "18px",
              padding: "0 8px",
              borderBottomRightRadius: 6,
              letterSpacing: 0.3,
            }}
          >
            {def?.label}
          </div>
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 6,
              display: "flex",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              margin: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Drag to reorder">
              <IconButton
                size="small"
                ref={setActivatorNodeRef}
                sx={{ color: "#475569", cursor: "grab", touchAction: "none" }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton
                size="small"
                onClick={() => onDuplicate(block.id)}
                sx={{ color: "#475569" }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onRemove(block.id)}
                sx={{ color: "#d32f2f" }}
              >
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </div>
        </>
      )}
      <div dangerouslySetInnerHTML={{ __html: renderBlockHtml(block, settings) }} />
    </div>
  );
}

export default function Canvas({
  doc,
  selectedId,
  onSelect,
  onDuplicate,
  onRemove,
}) {
  const s = doc.settings;
  const { setNodeRef, isOver } = useDroppable({ id: "canvas-root" });

  return (
    <div
      className="h-full w-full overflow-y-auto py-10 px-4"
      style={{ backgroundColor: s.backgroundColor }}
      onClick={() => onSelect(null)}
    >
      <div
        ref={setNodeRef}
        style={{
          margin: "0 auto",
          width: s.contentWidth,
          maxWidth: "100%",
          backgroundColor: s.cardColor,
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: isOver
            ? `0 0 0 2px ${BRAND}, 0 8px 24px rgba(6,157,110,0.18)`
            : "0 4px 16px rgba(15,23,42,0.08)",
          minHeight: 160,
          transition: "box-shadow 0.15s ease",
        }}
      >
        {doc.blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-400 py-20 gap-2">
            <AddPhotoAlternateIcon style={{ fontSize: 40, opacity: 0.4 }} />
            <div className="text-sm">
              Drag a block here, or click one in the left panel
            </div>
          </div>
        ) : (
          <SortableContext
            items={doc.blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {doc.blocks.map((block) => (
              <SortableBlock
                key={block.id}
                block={block}
                settings={s}
                selected={block.id === selectedId}
                onSelect={onSelect}
                onDuplicate={onDuplicate}
                onRemove={onRemove}
              />
            ))}
          </SortableContext>
        )}
      </div>
    </div>
  );
}
